// frontend/src/components/Layout/ProfileMenu.jsx

import React, { useState, useEffect } from "react";
import api from "../../../api";
import PropTypes from "prop-types";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "../../../Pages/Profile/Profile.css";
import "./ProfileMenu.css";
import {
  avatarState,
  usernameState,
} from "../../../Recoil/atom";
import { useRecoilValue } from "recoil";
import {
  getMiningLevel,
  getCatcherLevel,
  getFishingLevel,
  getFarmingLevel,
  getLevel,
} from "../../../constants/items";

const ProfileMenu = (props) => {
  const avatar = useRecoilValue(avatarState);
  const username = useRecoilValue(usernameState);

  const [money, setMoney] = useState(0);
  const [coins, setCoins] = useState(0);
  const [battleCoins, setBattleCoins] = useState(0);
  const [pokedex, setPokedex] = useState(0);
  const [miningLevel, setMiningLevel] = useState(0);
  const [batteLevel, setBatteLevel] = useState(0);
  const [catcherLevel, setCatcherLevel] = useState(0);
  const [fishingLevel, setFishingLevel] = useState(0);
  const [farmingLevel, setFarmingLevel] = useState(0);
  const [admValue, setAdmValue] = useState(0); // raw integer from server
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState("");

  // Three derived booleans:
  const isAdmin = admValue === 1;
  const isSpriter = admValue === 2;
  const isModerator = admValue === 4;

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);

    // Only fetch once per open
    if (!menuOpen && !loading) {
      setLoading(true);
      api
        .get("/v1/menu/details", { withCredentials: true })
        .then((response) => {
          if (response.data) {
            const data = response.data;

            setMoney(data.money);
            
            setCoins(data.coins);
            setPokedex(data.pokedex_count);

            // 'adm' is now returned as an integer
            setAdmValue(data.adm);

            setBatteLevel(data.trainer_exp)
            setCatcherLevel(data.catcher_exp);
            setBattleCoins(data.battle_coins);
            setMiningLevel(data.mining_exp);
            setFishingLevel(data.fishing_exp || 0);
            setFarmingLevel(data.farming_exp || 0);
            setPromo(data.current_promo || "");
          }
        })
        .catch((error) => {
          console.error("Error fetching menu details:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const avatarSrc = `/avatars/${avatar}.png`;
  const moneySrc = `/items/money.png`;
  const coinsSrc = `/items/coin.png`;
  const battleCoinsSrc = `/items/battle_coin.png`;
  const ballSrc = `/items/pokeball.png`;
  const pickSrc = `/items/NormalPickaxe.png`;
  const battleSrc = `/items/battleseek.png`;
  const fishSrc = `/items/superrod.png`;
  const farmingSrc = `/items/razzberry.png`;
  const dexSrc = `/items/pokedex.png`;
  const promoSrc = promo ? `/icon/${promo}.png` : "";

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menuOpen}
        toggle={toggleMenu}
        className="d-inline-block"
      >
        <DropdownToggle
          className="btn header-item"
          tag="button"
          id="page-header-user-dropdown"
        >
          <img
            className="rounded-circle header-profile-user"
            src={avatarSrc}
            alt="Header Avatar"
          />
          <span className="d-none d-xl-inline-block ms-2 me-2">
            {username}
          </span>
          <i className="mdi mdi-chevron-down d-none d-xl-inline-block" />
        </DropdownToggle>

        <DropdownMenu className="profile-menu-dropdown">
          {loading ? (
            <div className="profile-menu-loading">
              <Spinner size="sm" />
            </div>
          ) : (
            <>
              {/* Promote Center */}
              {promo && (
                <>
                  <DropdownItem
                    className="profile-menu-promo"
                    tag={Link}
                    to="/promo-center"
                  >
                    <img
                      alt={promo}
                      src={promoSrc}
                      className="profile-menu-promo-icon"
                    />
                    <span>Promo Center</span>
                  </DropdownItem>
                  <DropdownItem divider />
                </>
              )}

              {/* Currency Stats */}
              <div className="profile-menu-stats">
                <div className="profile-menu-stat-item">
                  <img src={moneySrc} alt="Money" />
                  <strong style={{ color: "#22c55e" }}>{money.toLocaleString()}</strong>
                </div>
                <div className="profile-menu-stat-item">
                  <img src={coinsSrc} alt="Coins" />
                  <strong style={{ color: "#eab308" }}>{coins.toLocaleString()}</strong>
                </div>
                <div className="profile-menu-stat-item">
                  <img src={battleCoinsSrc} alt="Battle Coins" />
                  <strong style={{ color: "#ef4444" }}>{battleCoins.toLocaleString()}</strong>
                </div>
                <div className="profile-menu-stat-item">
                  <img src={dexSrc} alt="Pokedex" />
                  <span style={{ color: "#3b82f6", fontWeight: "600" }}>{pokedex.toLocaleString()} Species</span>
                </div>
              </div>

              <DropdownItem divider />

              {/* Level Cards */}
              <div className="profile-menu-levels">
                <DropdownItem
                  className="profile-menu-level-item"
                  tag={Link}
                  to="/department-store"
                >
                  <img src={battleSrc} alt="Trainer" />
                  <div className="profile-menu-level-info">
                    <span className="profile-menu-level-label">Trainer</span>
                    <span className="profile-menu-level-value" style={{ color: "#ef4444" }}>Lv. {getLevel(batteLevel).toLocaleString()}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  className="profile-menu-level-item"
                  tag={Link}
                  to="/department-store"
                >
                  <img src={ballSrc} alt="Catcher" />
                  <div className="profile-menu-level-info">
                    <span className="profile-menu-level-label">Catcher</span>
                    <span className="profile-menu-level-value" style={{ color: "#22c55e" }}>Lv. {getCatcherLevel(catcherLevel).toLocaleString()}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  className="profile-menu-level-item"
                  tag={Link}
                  to="/department-store"
                >
                  <img src={pickSrc} alt="Mining" />
                  <div className="profile-menu-level-info">
                    <span className="profile-menu-level-label">Mining</span>
                    <span className="profile-menu-level-value" style={{ color: "#d97706" }}>Lv. {getMiningLevel(miningLevel).toLocaleString()}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  className="profile-menu-level-item"
                  tag={Link}
                  to="/map/FishingShop"
                >
                  <img src={fishSrc} alt="Fishing" />
                  <div className="profile-menu-level-info">
                    <span className="profile-menu-level-label">Fishing</span>
                    <span className="profile-menu-level-value" style={{ color: "#3b82f6" }}>Lv. {getFishingLevel(fishingLevel).toLocaleString()}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  className="profile-menu-level-item"
                  tag={Link}
                  to="/map/BerryShop"
                >
                  <img src={farmingSrc} alt="Farming" />
                  <div className="profile-menu-level-info">
                    <span className="profile-menu-level-label">Farming</span>
                    <span className="profile-menu-level-value" style={{ color: "#fb923c" }}>Lv. {getFarmingLevel(farmingLevel).toLocaleString()}</span>
                  </div>
                </DropdownItem>
              </div>
          

              {/* If admValue === 2, show Spriter Panel link */}
              {isSpriter && (
                <>
                  <DropdownItem divider />
                  <DropdownItem tag={Link} to="/spriter-dashboard">
                    <i className="ri-pencil-ruler-2-line align-middle me-2" />
                    {props.t("Spriter Panel")}
                  </DropdownItem>
                </>
              )}

              {/* If admValue === 4, show Moderator Panel link */}
              {isModerator && (
                <>
                  <DropdownItem divider />
                  <DropdownItem tag={Link} to="/moderator-panel">
                    <i className="ri-user-settings-line align-middle me-2" />
                    {props.t("Moderator Panel")}
                  </DropdownItem>
                </>
              )}

              {/* If admValue === 1, show Admin Panel link */}
              {isAdmin && (
                <>
                  <DropdownItem divider />
                  <DropdownItem tag={Link} to="/admin-panel">
                    <i className="ri-shield-user-line align-middle me-2" />
                    {props.t("Admin Panel")}
                  </DropdownItem>
                </>
              )}

              <DropdownItem divider />

              {/* Always show Profile & Settings & Logout */}
              <DropdownItem className="profile-menu-nav-item" tag={Link} to="/profile">
                <i className="ri-user-line align-middle me-2" />
                {props.t("Profile")}
              </DropdownItem>
              <DropdownItem className="profile-menu-nav-item" tag={Link} to="/settings">
                <i className="ri-settings-2-line align-middle me-2" />
                {props.t("Settings")}
              </DropdownItem>

              <DropdownItem divider />

              <DropdownItem className="profile-menu-nav-item profile-menu-logout" tag={Link} to="/logout">
                <i className="ri-shut-down-line align-middle me-2 text-danger" />
                {props.t("Logout")}
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

ProfileMenu.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(ProfileMenu);
